// src/pages/CartPage.tsx
import React, { useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, Button, Alert, Divider
} from '@mui/material';
import { Add, Remove, DeleteOutline } from '@mui/icons-material';
import { useCartStore } from '../../store/cartStore';
import { formatCurrency } from '../../utils/currency';

function formatDate(dateStr?: string | null) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr; // fallback if server sends non-ISO
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit'
  }).format(d);
}

export default function CartPage() {
  const { cartId } = useParams<{ cartId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    cart, loading, error, itemsCount, linesCount,
    loadCartById, refreshOpenCart, updateQty, removeItem,
    cancelOpenCart, placeOpenCart
  } = useCartStore();

  useEffect(() => {
    if (cartId) {
      void loadCartById(Number(cartId));
    } else {
      void refreshOpenCart();
    }
  }, [cartId, loadCartById, refreshOpenCart]);

  const totals = useMemo(() => {
    const subtotal = cart?.items?.reduce((s, i) => s + (i.quantity * (i.price ?? 0)), 0) ?? 0;
    return { subtotal };
  }, [cart]);

  const onInc = (cartItemId: number, qty: number) => updateQty(cartItemId, qty + 1);
  const onDec = (cartItemId: number, qty: number) => updateQty(cartItemId, Math.max(1, qty - 1));
  const onRemove = (cartItemId: number) => removeItem(cartItemId);

  const onCancel = async () => {
    await cancelOpenCart();
    navigate('/cart');
  };

  const onPlace = async () => {
    // navigate to checkout page for purchaser details
    const id = cart?.cartId ?? cartId;
    if (id) navigate(`/cart/${id}/checkout`);
  };

  const onContinueBrowsing = () => {
    const from = String((location.state as { from?: string } | null)?.from || "");
    const isSafeFrom =
      from &&
      from !== "/" &&
      !from.startsWith("/cart") &&
      !from.startsWith("/forgot-password");

    navigate(isSafeFrom ? from : "/components");
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Loading cart…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const empty = !cart || !cart.items || cart.items.length === 0 || itemsCount === 0;

  if (empty) {
    return (
      <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
        <Typography variant="h5" sx={{ mb: 1 }}>Cart</Typography>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Cart is empty</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You haven’t added any parts yet.
          </Typography>
          <Button variant="contained" onClick={onContinueBrowsing}>
            Continue browsing
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Cart
      </Typography>

      {/* 🔷 Banner with Cart # and Created on */}
      <Box
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #1f8ef1, #20bda5)',
          color: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Cart #{cart?.cartId}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.95 }}>
          Created on&nbsp;{formatDate(cart?.createdOn)}
        </Typography>
      </Box>

      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 140 }}>Part No.</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right" sx={{ width: 90 }}>UOM</TableCell>
              <TableCell align="right" sx={{ width: 120 }}>Price</TableCell>
              <TableCell align="center" sx={{ width: 150 }}>Qty</TableCell>
              <TableCell align="right" sx={{ width: 140 }}>Line Total</TableCell>
              <TableCell align="center" sx={{ width: 80 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cart!.items.map(line => (
              <TableRow key={line.cartItemId}>
                <TableCell>{line.partNumber}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{line.description}</Typography>
                  {line.itemNo ? <Typography variant="caption" color="text.secondary">Item #{line.itemNo}</Typography> : null}
                </TableCell>
                <TableCell align="right">{line.uom ?? '-'}</TableCell>
                <TableCell align="right">{formatCurrency(line.price)}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => onDec(line.cartItemId, line.quantity)}><Remove fontSize="small" /></IconButton>
                  <Typography component="span" sx={{ mx: 1, display: 'inline-block', minWidth: 24 }}>
                    {line.quantity}
                  </Typography>
                  <IconButton size="small" onClick={() => onInc(line.cartItemId, line.quantity)}><Add fontSize="small" /></IconButton>
                </TableCell>
                <TableCell align="right">{formatCurrency((line.price ?? 0) * line.quantity)}</TableCell>
                <TableCell align="center">
                  <IconButton color="error" onClick={() => onRemove(line.cartItemId)}>
                    <DeleteOutline />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={7}>
                <Divider />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={3} />
              <TableCell align="right" sx={{ fontWeight: 600 }}>Lines:</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>{linesCount}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Subtotal: {formatCurrency(totals.subtotal)}</TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, p: 2 }}>
          <Button variant="outlined" color="inherit" onClick={onContinueBrowsing}>
            Continue browsing
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" color="error" onClick={onCancel}>
              Cancel Cart
            </Button>
            <Button variant="contained" onClick={onPlace}>
              Place Cart
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
